// Generated from Swagger on 2025-12-04T17:43:47.150Z
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
  UseSuspenseQueryOptions,
  UseQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useContext } from "react";
import { useApiClient } from "../client";
import { creationEntitiesKeys } from "../creation-entities-keys";
import { formatSearchQuery } from "../../utils/format-search-query";
import { Context } from "@/util-components/root/context";
import {
  ApplyManagedImageEditRequestDto,
  ManagedImageResponseDto,
} from "../interfaces/media.interface";
import {
  ApplySponsorImageEditRequestDto,
  SponsorListPaginatedResponseDto,
  CreateSponsorRequestDto,
  SponsorResponseDto,
  SponsorCreationEntitiesResponseDto,
  SponsorKpiResponseDto,
  SponsorPostDetailsResponseDto,
  SponsorDetailsResponseDto,
  SponsorsUploadImageFormData,
  UpdateSponsorRequestDto,
  USponsorsuploadAgreementFormData,
} from "../interfaces/sponsors.interface";
import {
  SponsorConversationLogResponseDto,
  CreateSponsorConversationLogRequestDto,
  UpdateSponsorConversationLogRequestDto,
} from "../interfaces/sponsor-conversation-logs.interface";

export interface UseSponsorsFindParams {
  /**
   * The text to search for
   * @constraints minLength: 4, example: Jobdescription
   */
  textSearch?: string;
  /**
   * Filter sponsors by advertisement (Leistung) IDs
   */
  advertisementIds?: string[];
  /**
   * The field to sort by
   */
  sortBy?: "createdAt" | "updatedAt" | "volume" | "name";
  /**
   * The order to sort by
   */
  sortOrder?: "asc" | "desc";
  /**
   * Filter sponsors by assigned user ID
   */
  assignedUserId?: string;
  /**
   * Filter sponsors by city
   */
  city?: string;
  /**
   * Filter sponsors by zip code
   */
  zip?: string;
  /**
   * Filter sponsors by active agreement status
   */
  hasActiveAgreement?: boolean;
  /**
   * The maximum amount of retrieved items per page
   * @constraints minimum: 1, maximum: 20, default: 20, example: 20
   */
  limit?: number;
  /**
   * The page number to retrieve items from.
   * @constraints minimum: 1, default: 1, example: 1
   */
  page?: number;
}

// Query keys for sponsors-related queries
export const sponsorsKeys = {
  all: ["sponsors"] as const,
  profile: () => [...sponsorsKeys.all, "profile"] as const,
  mutation: () => [...sponsorsKeys.all, "mutation"] as const,
  creation_entities: () => [...sponsorsKeys.all, "creation_entities"] as const,
  kpis: () => [...sponsorsKeys.all, "kpis"] as const,
  mail_templates_detail: (params?: any) =>
    [...sponsorsKeys.all, "mail_templates_detail", params] as const,
  post: () => [...sponsorsKeys.all, "post"] as const,
  post_detail: (params?: any) =>
    [...sponsorsKeys.all, "post_detail", params] as const,
  details: () => [...sponsorsKeys.all, "detail"] as const,
  detail: (id: string) => [...sponsorsKeys.details(), id] as const,
  detail_mutation: (params?: any) =>
    [...sponsorsKeys.all, "detail_mutation", params] as const,
  detail_image_mutation: (params?: any) =>
    [...sponsorsKeys.all, "detail_image_mutation", params] as const,
  detail_agreement_mutation: (params?: any) =>
    [...sponsorsKeys.all, "detail_agreement_mutation", params] as const,
  conversation_logs: (id: string) =>
    [...sponsorsKeys.detail(id), "conversation_logs"] as const,
  conversation_logs_all: () =>
    [...sponsorsKeys.all, "conversation_logs"] as const,
};

export function invalidateSponsorsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as string[];
      return queryKey.includes("creation-entities");
    },
  });
}

/**
 * Find all sponsors with filter
 */
export function useSponsorsFind(
  params?: UseSponsorsFindParams,
  options?: Omit<
    UseSuspenseQueryOptions<SponsorListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...sponsorsKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<SponsorListPaginatedResponseDto>(
        `/sponsors${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsFind
 */
export function useSponsorsFindQuery(
  params?: UseSponsorsFindParams,
  options?: Omit<
    UseQueryOptions<SponsorListPaginatedResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...sponsorsKeys.profile(),
      ...creationEntitiesKeys.list(),
      params,
    ],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<SponsorListPaginatedResponseDto>(
        `/sponsors${queryString}`,
      );
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Create a new sponsor
 */
export function useSponsorsCreate(
  options?: Omit<
    UseMutationOptions<SponsorResponseDto, Error, CreateSponsorRequestDto>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();
  const { refetchUser } = useContext(Context);

  return useMutation({
    // User-Options zuerst, damit unsere internen Handler nicht überschrieben werden
    ...options,
    mutationFn: (data: CreateSponsorRequestDto) =>
      apiRequest<SponsorResponseDto>(`/sponsors`, "POST", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      void refetchUser?.();
      // Danach evtl. vom Nutzer übergebenes onSuccess ausführen
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get creation entities
 */
export function useSponsorsCreationEntities(
  options?: Omit<
    UseSuspenseQueryOptions<SponsorCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<SponsorCreationEntitiesResponseDto>(
        `/sponsors/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsCreationEntities
 */
export function useSponsorsCreationEntitiesQuery(
  options?: Omit<
    UseQueryOptions<SponsorCreationEntitiesResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: creationEntitiesKeys.list(),
    queryFn: () =>
      apiRequest<SponsorCreationEntitiesResponseDto>(
        `/sponsors/creation-entities`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get sponsor KPIs
 */
export function useSponsorsGetKpis(
  params?: { trendPeriod?: 'year' | 'month' | 'quarter'; department?: string },
  options?: Omit<
    UseSuspenseQueryOptions<SponsorKpiResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...sponsorsKeys.kpis(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<SponsorKpiResponseDto>(`/sponsors/kpis${queryString}`);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsGetKpis
 */
export function useSponsorsGetKpisQuery(
  params?: { trendPeriod?: 'year' | 'month' | 'quarter'; department?: string },
  options?: Omit<
    UseQueryOptions<SponsorKpiResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...sponsorsKeys.kpis(), ...creationEntitiesKeys.list(), params],
    queryFn: () => {
      const queryString = formatSearchQuery(params);
      return apiRequest<SponsorKpiResponseDto>(`/sponsors/kpis${queryString}`);
    },
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get mail templates
 */
export function useSponsorsGetMailTemplates(
  sponsorId: string,
  options?: Omit<UseSuspenseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...sponsorsKeys.mail_templates_detail({ sponsorId }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => apiRequest<any>(`/sponsors/mail-templates/${sponsorId}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsGetMailTemplates
 */
export function useSponsorsGetMailTemplatesQuery(
  sponsorId: string,
  options?: Omit<UseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...sponsorsKeys.mail_templates_detail({ sponsorId }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () => apiRequest<any>(`/sponsors/mail-templates/${sponsorId}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get sponsors for post
 */
export function useSponsorsGetSponsorsForPost(
  options?: Omit<UseSuspenseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...sponsorsKeys.post(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<any>(`/sponsors/post`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsGetSponsorsForPost
 */
export function useSponsorsGetSponsorsForPostQuery(
  options?: Omit<UseQueryOptions<any>, "queryKey" | "queryFn">,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...sponsorsKeys.post(), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<any>(`/sponsors/post`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Get sponsor for post
 */
export function useSponsorsGetSponsorForPost(
  sponsorId: string,
  options?: Omit<
    UseSuspenseQueryOptions<SponsorPostDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [
      ...sponsorsKeys.post_detail({ sponsorId }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () =>
      apiRequest<SponsorPostDetailsResponseDto>(`/sponsors/post/${sponsorId}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsGetSponsorForPost
 */
export function useSponsorsGetSponsorForPostQuery(
  sponsorId: string,
  options?: Omit<
    UseQueryOptions<SponsorPostDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [
      ...sponsorsKeys.post_detail({ sponsorId }),
      ...creationEntitiesKeys.list(),
    ],
    queryFn: () =>
      apiRequest<SponsorPostDetailsResponseDto>(`/sponsors/post/${sponsorId}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Find one sponsor
 */
export function useSponsorsFindOne(
  id: string,
  options?: Omit<
    UseSuspenseQueryOptions<SponsorDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...sponsorsKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<SponsorDetailsResponseDto>(`/sponsors/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorsFindOne
 */
export function useSponsorsFindOneQuery(
  id: string,
  options?: Omit<
    UseQueryOptions<SponsorDetailsResponseDto>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...sponsorsKeys.detail(id), ...creationEntitiesKeys.list()],
    queryFn: () => apiRequest<SponsorDetailsResponseDto>(`/sponsors/${id}`),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Update one sponsor
 */
export function useSponsorsUpdate(
  options?: Omit<
    UseMutationOptions<
      SponsorDetailsResponseDto,
      Error,
      { id: string; data: UpdateSponsorRequestDto }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: UpdateSponsorRequestDto }) =>
      apiRequest<SponsorDetailsResponseDto>(`/sponsors/${id}`, "PATCH", data),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete one sponsor
 */
export function useSponsorsDelete(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/sponsors/${id}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete multiple sponsors
 */
export function useSponsorsBulkDelete(
  options?: Omit<UseMutationOptions<void, Error, { ids: string[] }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ ids }: { ids: string[] }) =>
      apiRequest<void>(`/sponsors/bulk-delete`, "POST", { ids }),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Upload Sponsor Image - Max 10MB
 */
export function useSponsorsUploadImage(
  options?: Omit<
    UseMutationOptions<
      ManagedImageResponseDto,
      Error,
      { id: string; data: SponsorsUploadImageFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SponsorsUploadImageFormData;
    }) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      return apiRequest<ManagedImageResponseDto>(`/sponsors/${id}/image`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidateSponsorsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete Sponsor Image
 */
export function useSponsorsDeleteImage(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/sponsors/${id}/image`, "DELETE"),
    onSuccess: (data, variables, context) => {
      invalidateSponsorsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useSponsorsApplyImageEdit(
  options?: Omit<
    UseMutationOptions<ManagedImageResponseDto, Error, { id: string; data: ApplySponsorImageEditRequestDto }>,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, data }: { id: string; data: ApplyManagedImageEditRequestDto }) =>
      apiRequest<ManagedImageResponseDto>(`/sponsors/${id}/image/edit`, "POST", data),
    onSuccess: (data, variables, context) => {
      invalidateSponsorsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useSponsorsRestoreOriginalImage(
  options?: Omit<UseMutationOptions<ManagedImageResponseDto, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<ManagedImageResponseDto>(`/sponsors/${id}/image/restore-original`, "POST"),
    onSuccess: (data, variables, context) => {
      invalidateSponsorsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

export function useSponsorsPreviewBackgroundRemoval(
  options?: Omit<UseMutationOptions<Blob, Error, { file: File }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();

  return useMutation({
    ...options,
    mutationFn: ({ file }: { file: File }) => {
      const formData = new FormData();
      formData.append("file", file);

      return apiRequest<Blob>(
        `/sponsors/media/preview-background-removal`,
        "POST",
        formData,
        undefined,
        { responseType: "blob", throwOnError: true }
      );
    },
  });
}

export function useSponsorsuploadImage(
  options?: Omit<
    UseMutationOptions<
      ManagedImageResponseDto,
      Error,
      { id: string; data: SponsorsUploadImageFormData }
    >,
    "mutationFn"
  >,
) {
  return useSponsorsUploadImage(options);
}

/**
 * Upload Sponsor Agreement - Max 10MB
 */
export function useSponsorsuploadAgreement(
  options?: Omit<
    UseMutationOptions<
      any,
      Error,
      { id: string; data: USponsorsuploadAgreementFormData }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: USponsorsuploadAgreementFormData;
    }) => {
      const formData = new FormData();
      if (data.file) formData.append("file", data.file);
      return apiRequest<any>(`/sponsors/${id}/agreement`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      invalidateSponsorsQueries(queryClient);
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete Sponsor Agreement
 */
export function useSponsorsDeleteAgreement(
  options?: Omit<UseMutationOptions<any, Error, { id: string }>, "mutationFn">,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id }: { id: string }) =>
      apiRequest<any>(`/sponsors/${id}/agreement`, "DELETE"),
    onSuccess: (data, variables, context) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      // Invalidate all queries that contain creation entities keys
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Upload Contact Avatar - Max 5MB
 */
export function useSponsorsUploadContactAvatar(
  options?: Omit<
    UseMutationOptions<
      any,
      Error,
      { id: string; contactId: string; file: File }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ id, contactId, file }: { id: string; contactId: string; file: File }) => {
      const formData = new FormData();
      formData.append("file", file);
      return apiRequest<any>(`/sponsors/${id}/contact/${contactId}/avatar`, "POST", formData);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.all });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as string[];
          return queryKey.includes("creation-entities");
        },
      });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete conversation log entry for sponsor
 */
export function useSponsorConversationLogDelete(
  sponsorId: string,
  options?: Omit<UseMutationOptions<void, Error, { logId: string }>, "mutationFn">
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ logId }: { logId: string }) =>
      apiRequest<void>(`/sponsors/${sponsorId}/conversation-logs/${logId}`, "DELETE"),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: sponsorsKeys.conversation_logs(sponsorId) });
      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Get sponsor conversation logs
 */
export function useSponsorConversationLogs(
  sponsorId: string,
  options?: Omit<
    UseSuspenseQueryOptions<SponsorConversationLogResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useSuspenseQuery({
    queryKey: [...sponsorsKeys.conversation_logs(sponsorId)],
    queryFn: () =>
      apiRequest<SponsorConversationLogResponseDto[]>(
        `/sponsors/${sponsorId}/conversation-logs`,
      ),
    // Default cache-disabling options (can be overridden via options)
    staleTime: 0,
    gcTime: 0, // replaces cacheTime in v5
    refetchOnMount: "always",
    ...options,
  });
}

/**
 * Non-suspense version of useSponsorConversationLogs
 */
export function useSponsorConversationLogsQuery(
  sponsorId: string,
  options?: Omit<
    UseQueryOptions<SponsorConversationLogResponseDto[]>,
    "queryKey" | "queryFn"
  >,
) {
  const { apiRequest } = useApiClient();

  return useQuery({
    queryKey: [...sponsorsKeys.conversation_logs(sponsorId)],
    queryFn: () =>
      apiRequest<SponsorConversationLogResponseDto[]>(
        `/sponsors/${sponsorId}/conversation-logs`,
      ),
    enabled: !!sponsorId,
    ...options,
  });
}

/**
 * Create a new sponsor conversation log
 */
export function useSponsorConversationLogCreate(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<
      SponsorConversationLogResponseDto,
      Error,
      CreateSponsorConversationLogRequestDto
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    // Spread user options FIRST so our handlers below cannot be overridden.
    // We'll call any user-provided callbacks from within our onSuccess.
    ...options,
    mutationFn: (data: CreateSponsorConversationLogRequestDto) =>
      apiRequest<SponsorConversationLogResponseDto>(
        `/sponsors/${sponsorId}/conversation-logs`,
        "POST",
        data,
      ),
    onSuccess: (data, variables, context) => {
      // Direkt Query-Daten updaten, damit der neue Eintrag ohne Reload sichtbar ist
      queryClient.setQueryData<SponsorConversationLogResponseDto[] | undefined>(
        [
          ...sponsorsKeys.conversation_logs(sponsorId),
        ],
        (old) => {
          const existing = Array.isArray(old) ? old : [];
          return [data, ...existing];
        },
      );

      // Fallback: relevante Queries invalidieren
      queryClient.invalidateQueries({
        queryKey: sponsorsKeys.conversation_logs(sponsorId),
      });
      queryClient.invalidateQueries({
        queryKey: sponsorsKeys.conversation_logs_all(),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Update an existing sponsor conversation log
 */
export function useSponsorConversationLogUpdate(
  sponsorId: string,
  options?: Omit<
    UseMutationOptions<
      SponsorConversationLogResponseDto,
      Error,
      { logId: string; data: UpdateSponsorConversationLogRequestDto }
    >,
    "mutationFn"
  >,
) {
  const { apiRequest } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ logId, data }) =>
      apiRequest<SponsorConversationLogResponseDto>(
        `/sponsors/${sponsorId}/conversation-logs/${logId}`,
        "PATCH",
        data,
      ),
    onSuccess: (data, variables, context) => {
      // Optimistic-like local update of the specific log entry in cache
      queryClient.setQueryData<SponsorConversationLogResponseDto[] | undefined>(
        [...sponsorsKeys.conversation_logs(sponsorId)],
        (old) => {
          if (!Array.isArray(old)) return old;
          return old.map((log) => (log._id === data._id ? data : log));
        },
      );

      // Fallback: invalidate related queries
      queryClient.invalidateQueries({
        queryKey: sponsorsKeys.conversation_logs(sponsorId),
      });
      queryClient.invalidateQueries({
        queryKey: sponsorsKeys.conversation_logs_all(),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}
